import { FAILURE_REPORT_ERROR_CODES, type FailureReportInput } from "@repo/shared-types/failure-reports";

const REPEAT_WINDOW_MS = 30_000;
const MAX_DURATION_MS = 86_400_000;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const OPERATION_PATTERN = /^\/[A-Za-z0-9/:_.*-]*$/;
const MAX_OPERATION_LENGTH = 200;
const LITERAL_SEGMENT = /^[a-z]+(?:-[a-z]+)*$/;
const METHODS = new Set<string>(["GET", "POST", "PUT", "PATCH", "DELETE", "HEAD", "OPTIONS"]);
const ERROR_CODES = new Set<string>(FAILURE_REPORT_ERROR_CODES);
const listeners = new Set<() => void>();
let current: FailureReportInput | null = null;

export type RequestFailureDetails = {
  path: string;
  method?: string;
  route?: string | null;
  status: number | null;
  body?: unknown;
  errorCode?: FailureReportInput["errorCode"];
  durationMs: number;
  correlationId?: string | null;
};

function notify(): void {
  for (const listener of listeners) {
    try {
      listener();
    } catch {
      // Diagnostics must not alter the failed request's behavior.
    }
  }
}

function browserFamily(): FailureReportInput["browser"] {
  const agent = globalThis.navigator?.userAgent ?? "";
  if (/Edg\//.test(agent)) return "Edge";
  if (/Firefox\//.test(agent)) return "Firefox";
  if (/Chrome\//.test(agent)) return "Chrome";
  if (/Safari\//.test(agent)) return "Safari";
  return "Other";
}

const isOperation = (value: string | null | undefined): value is string => !!value && value.length <= MAX_OPERATION_LENGTH && OPERATION_PATTERN.test(value);

// Responses from a gateway (e.g. a timeout) carry no route header, so derive one from the path with values masked.
export function operationFromPath(path: string): string | null {
  const pathname = path.split(/[?#]/, 1)[0] ?? "";
  if (!pathname.startsWith("/")) return null;
  const template = pathname
    .split("/")
    .map((segment) => (segment === "" || LITERAL_SEGMENT.test(segment) ? segment : ":param"))
    .join("/");
  return isOperation(template) ? template : null;
}

function errorCode(details: RequestFailureDetails): FailureReportInput["errorCode"] {
  const value = details.errorCode ?? (typeof details.body === "object" && details.body !== null && "error" in details.body ? details.body.error : undefined);
  return typeof value === "string" && ERROR_CODES.has(value) ? (value as FailureReportInput["errorCode"]) : undefined;
}

export function getRequestFailure(): FailureReportInput | null {
  return current;
}

export function subscribeRequestFailures(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function dismissRequestFailure(reportId?: string): void {
  if (!current || (reportId !== undefined && reportId !== current.reportId)) return;
  current = null;
  notify();
}

export function recordRequestFailure(details: RequestFailureDetails): void {
  try {
    if ((details.status !== null && details.status >= 400 && details.status < 500) || /\/api\/(?:admin\/)?failure-reports(?:[/?]|$)/.test(details.path)) return;
    const operation = isOperation(details.route) ? details.route : (operationFromPath(details.path) ?? "/unknown");
    const code = errorCode(details);
    const requestedMethod = (details.method ?? "GET").toUpperCase();
    const method = METHODS.has(requestedMethod) ? (requestedMethod as FailureReportInput["method"]) : "GET";
    const status = details.status !== null && details.status >= 100 && details.status <= 599 ? details.status : null;
    const now = Date.now();
    if (current && now - Date.parse(current.occurredAt) < REPEAT_WINDOW_MS && current.operation === operation && current.method === method && current.status === status && current.errorCode === code)
      return;
    current = {
      reportId: crypto.randomUUID(),
      occurredAt: new Date(now).toISOString(),
      operation,
      method,
      status,
      ...(code === undefined ? {} : { errorCode: code }),
      durationMs: Number.isFinite(details.durationMs) ? Math.max(0, Math.min(MAX_DURATION_MS, Math.round(details.durationMs))) : 0,
      browser: browserFamily(),
      online: globalThis.navigator?.onLine ?? true,
      ...(details.correlationId && UUID_PATTERN.test(details.correlationId) ? { correlationId: details.correlationId } : {}),
    };
    notify();
  } catch {
    // Capturing a report must never replace the original error.
  }
}
